import React from 'react';
import { useTheme } from '@mui/material/styles';
import { io, Socket } from 'socket.io-client';

import { CARD_COMMENT_SOCKET_URL, CARD_DETAILS_SOCKET_URL, SOCKET_URL } from '@app/configs';

export function HighlightMentions({ text }: { text: string }) {
  const theme = useTheme();

  if (!text) return null;

  return (
    <>
      {text.split(/(\s+)/).map((part: string, index: number) => {
        if (part.startsWith('@')) {
          return (
            <span key={index} style={{ color: theme.palette.common.black, fontWeight: 600 }}>
              {part}
            </span>
          );
        }
        return <React.Fragment key={index}>{part}</React.Fragment>;
      })}
    </>
  );
}
export function parseMentions(content: string): {
  name: string;
  mentionedUserIds: number[];
} {
  const mentionRegex = /@\[(.*?)\]\((\d+)\)/g;
  const mentionedUserIds: number[] = [];

  const plainText = content.replace(mentionRegex, (_: string, name: string, id: string) => {
    mentionedUserIds.push(Number(id));
    return `@${name}`;
  });

  return {
    name: plainText,
    mentionedUserIds,
  };
}

// Task Board Socket
let socket: Socket;

export const connectToBoardSocket = (
  boardId: number,
  onUpdate: (data: any) => void,
  onStatusChange?: (status: string) => void,
  onJoin?: (data: any) => void
) => {
  if (!socket?.connected) {
    socket = io(SOCKET_URL, {
      transports: ['websocket'],
      autoConnect: false,
    });

    socket.on('connect', () => {
      onStatusChange?.('Connected');
      socket.emit('board:join', boardId);
    });

    socket.on('disconnect', () => {
      onStatusChange?.('Disconnected');
    });

    socket.on('connect_error', (err) => {
      console.error('Connection error:', err);
    });

    socket.on('board:update', (data) => {
      onUpdate(data);
    });

    socket.connect();
  }
};

export const disconnectBoardSocket = () => {
  if (socket) {
    socket.disconnect();
    socket.removeAllListeners();
  }
};

export const emitBoardUpdate = (updatePayload: any) => {
  if (socket?.connected) {
    socket.emit('board:update', updatePayload);
  }
};

// Card Comments Socket

let commentSocket: Socket;

export const connectToCommentSocket = (
  cardId: string,
  onUpdate: (data: any) => void,
  onStatusChange?: (status: string) => void
) => {
  if (!commentSocket?.connected) {
    commentSocket = io(CARD_COMMENT_SOCKET_URL, {
      transports: ['websocket'],
      autoConnect: false,
    });

    commentSocket.on('connect', () => {
      onStatusChange?.('connected');
      commentSocket.emit('card:join', cardId);
    });

    commentSocket.on('disconnect', () => {
      onStatusChange?.('disconnected');
    });

    commentSocket.on('connect_error', (err) => {
      console.error('Connection error:', err);
    });

    commentSocket.on('comments:update', (commentData) => {
      onUpdate(commentData);
    });

    commentSocket.connect();
  }
};

export const disconnectCommentSocket = () => {
  if (commentSocket) {
    commentSocket.disconnect();
    commentSocket.removeAllListeners();
  }
};

export const emitCommentUpdate = (payload: any) => {
  if (commentSocket?.connected) {
    commentSocket.emit('comments:update', payload);
  }
};

// Card Details Socket

let detailsSocket: Socket;

export const connectToDetailsSocket = (
  cardId: string,
  onUpdate: (data: any) => void,
  onStatusChange?: (status: string) => void
) => {
  if (!detailsSocket?.connected) {
    detailsSocket = io(CARD_DETAILS_SOCKET_URL, {
      transports: ['websocket'],
      autoConnect: false,
    });

    detailsSocket.on('connect', () => {
      onStatusChange?.('connected');
      detailsSocket.emit('card:join', cardId);
    });

    detailsSocket.on('disconnect', () => {
      onStatusChange?.('disconnected');
    });

    detailsSocket.on('connect_error', (err) => {
      console.error('Connection error:', err);
    });

    detailsSocket.on('card:update', (cardDetails) => {
      onUpdate(cardDetails);
    });

    detailsSocket.connect();
  }
};

export const disconnectDetailsSocket = () => {
  if (detailsSocket) {
    detailsSocket.disconnect();
    detailsSocket.removeAllListeners();
  }
};

export const emitDetailsUpdate = (payload: any) => {
  if (detailsSocket?.connected) {
    detailsSocket.emit('card:update', payload);
  }
};
